import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { useAccessToken } from './useProfile';

interface ActivityStreamPoint {
  time?: number;
  distance?: number;
  altitude?: number;
  heartRate?: number;
  cadence?: number;
  velocity?: number;
  latitude?: number;
  longitude?: number;
}

interface ActivityInterval {
  name?: string;
  startTime: number;
  elapsedTime: number;
  distance?: number;
  averageSpeed?: number;
}

interface WorkoutData {
  id: number;
  title: string;
  description?: string;
  type?: string;
  distance?: number;
  duration?: number;
  movingTime?: number;
  elapsedTime?: number;
  averageSpeed?: number;
  maxSpeed?: number;
  maxHeartRate?: number;
  averageHeartRate?: number;
  averageCadence?: number;
  activityStreams?: ActivityStreamPoint[] | null;
  activityIntervals?: ActivityInterval[] | null;
  completedAt?: number;
  userId: number;
  activityId: string;
  polylineEncoded?: string;
  startLatitude?: number;
  startLongitude?: number;
  endLatitude?: number;
  endLongitude?: number;
  elevationGain?: number;
  elevationLow?: number;
  elevationHigh?: number;
  planned_workout_id?: number | null;
}

const WORKOUT_STALE_TIME = 24 * 60 * 60 * 1000; // 1 day

export function useWorkout(
  activityId: string,
): UseQueryResult<WorkoutData | null> {
  const accessToken = useAccessToken();

  return useQuery({
    queryKey: ['workout', activityId],
    queryFn: async () => {
      const response = await fetch(
        `${import.meta.env.REACT_APP_API_SERVICE_URI}/api/workouts/${activityId}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken.data}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch workout: ${response.status}`);
      }

      const result = await response.json();
      return {
        ...result.activity,
        planned_workout_id: result.planned_workout_id ?? null,
      } as WorkoutData;
    },
    enabled: !!activityId && !!accessToken.data,
    staleTime: WORKOUT_STALE_TIME,
    refetchOnMount: true,
  });
}
